import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Loader2, Plus, Pencil, Trash2, Upload as UploadIcon, X, Eye, EyeOff, ImageOff } from 'lucide-react';
import api, { resolveImageUrl } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';

const emptyForm = {
  id: null,
  title: '',
  excerpt: '',
  content: '',
  thumbnail: '',
  category: 'News',
  author: 'Werdi Media',
  published: true,
  publish_date: new Date().toISOString().slice(0, 10),
};

const CATEGORIES = ['News', 'Tutorial', 'Copyright', 'Tips', 'Interview', 'Industry', 'Lainnya'];

const BlogManager = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [contentUploading, setContentUploading] = useState(false);
  const quillRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/blog');
      setItems(data);
    } catch {
      toast({ title: 'Gagal memuat data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setModal(true);
  };

  const openEdit = (r) => {
    setForm({ ...r, publish_date: (r.publish_date || '').slice(0, 10) });
    setModal(true);
  };

  const uploadAndGetUrl = useCallback(async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post('/admin/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    // Store as relative /api/uploads/... in DB so it stays portable across domains.
    return data.url;
  }, []);

  const insertImageInEditor = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setContentUploading(true);
      try {
        const url = await uploadAndGetUrl(file);
        const editor = quillRef.current?.getEditor();
        if (editor) {
          const range = editor.getSelection(true);
          // Render preview via absolute URL so admin sees it immediately;
          // the stored HTML keeps the relative URL because BlogDetailPage rewrites at render time.
          const previewUrl = resolveImageUrl(url);
          editor.insertEmbed(range.index, 'image', previewUrl, 'user');
          editor.setSelection(range.index + 1);
        }
        toast({ title: 'Gambar disisipkan ke artikel' });
      } catch (err) {
        toast({
          title: 'Gagal upload gambar',
          description: err?.response?.data?.detail || 'Terjadi kesalahan',
          variant: 'destructive',
        });
      } finally {
        setContentUploading(false);
      }
    };
    input.click();
  }, [toast, uploadAndGetUrl]);

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [2, 3, 4, false] }],
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['blockquote', 'link', 'image'],
          ['clean'],
        ],
        handlers: {
          image: insertImageInEditor,
        },
      },
      clipboard: { matchVisual: false },
    }),
    [insertImageInEditor]
  );

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadAndGetUrl(file);
      setForm((f) => ({ ...f, thumbnail: url }));
      toast({ title: 'Upload berhasil' });
    } catch (err) {
      toast({
        title: 'Upload gagal',
        description: err?.response?.data?.detail || 'Terjadi kesalahan',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title || !form.publish_date) {
      toast({ title: 'Lengkapi data wajib', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.slug;
      delete payload.created_at;
      delete payload.updated_at;

      if (form.id) {
        await api.put(`/admin/blog/${form.id}`, payload);
        toast({ title: 'Artikel diperbarui' });
      } else {
        await api.post('/admin/blog', payload);
        toast({ title: 'Artikel ditambahkan' });
      }
      setModal(false);
      await load();
    } catch (err) {
      toast({
        title: 'Gagal menyimpan',
        description: err?.response?.data?.detail || 'Terjadi kesalahan',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus artikel ini?')) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      toast({ title: 'Artikel dihapus' });
      await load();
    } catch {
      toast({ title: 'Gagal menghapus', variant: 'destructive' });
    }
  };

  const togglePublished = async (r) => {
    try {
      await api.put(`/admin/blog/${r.id}`, { published: !r.published });
      await load();
    } catch {
      toast({ title: 'Gagal mengubah status', variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-white font-bold text-2xl tracking-tight">Blog & Artikel</h2>
          <p className="text-neutral-500 text-sm mt-1">Kelola artikel & tutorial di website</p>
        </div>
        <Button
          onClick={openCreate}
          className="rounded-full bg-gradient-to-r from-[#E9C46A] to-[#D4A94F] text-black font-semibold btn-transition gold-shimmer"
        >
          <Plus className="w-4 h-4 mr-1" /> Tulis Artikel
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-[#E9C46A] animate-spin" />
        </div>
      ) : (
        <div className="space-y-3">
          {items.length === 0 && (
            <div className="text-center py-16 text-neutral-500 border border-dashed border-white/10 rounded-2xl">
              Belum ada artikel. Klik &quot;Tulis Artikel&quot; untuk membuat.
            </div>
          )}
          {items.map((r) => (
            <div
              key={r.id}
              className="bg-[#0f0f0f] border border-white/[0.06] rounded-2xl p-4 flex gap-4"
            >
              <div className="w-28 h-20 rounded-lg overflow-hidden bg-neutral-900 flex-shrink-0 flex items-center justify-center">
                {r.thumbnail ? (
                  <img
                    src={resolveImageUrl(r.thumbnail)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageOff className="w-5 h-5 text-neutral-700" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold truncate">{r.title}</h3>
                    <p className="text-neutral-500 text-sm truncate mt-0.5">{r.excerpt}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs">
                      <span className="px-2 py-0.5 rounded-full bg-[#D4A94F]/15 text-[#E9C46A] font-semibold">
                        {r.category}
                      </span>
                      <span className="text-neutral-500">{r.publish_date}</span>
                      <span className="text-neutral-600">• {r.author}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span
                      className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                        r.published
                          ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25'
                          : 'bg-neutral-500/15 text-neutral-400 border border-neutral-500/25'
                      }`}
                    >
                      {r.published ? 'Live' : 'Draft'}
                    </span>
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-neutral-400 hover:text-[#E9C46A]" onClick={() => togglePublished(r)}>
                      {r.published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-neutral-400 hover:text-[#E9C46A]" onClick={() => openEdit(r)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-neutral-400 hover:text-red-400" onClick={() => handleDelete(r.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div
          className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0d0d0d] border border-[#D4A94F]/30 rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
              <h3 className="text-white font-bold text-xl">
                {form.id ? 'Edit Artikel' : 'Artikel Baru'}
              </h3>
              <button onClick={() => setModal(false)} className="text-neutral-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                  Judul *
                </label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white mt-1.5"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                    Kategori
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full mt-1.5 h-10 rounded-md bg-black/50 border border-white/10 text-white px-3 focus:outline-none focus:ring-2 focus:ring-[#D4A94F]"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                    Penulis
                  </label>
                  <Input
                    value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                    className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white mt-1.5"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                    Tanggal *
                  </label>
                  <Input
                    type="date"
                    value={form.publish_date}
                    onChange={(e) => setForm({ ...form, publish_date: e.target.value })}
                    className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white mt-1.5"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                  Thumbnail
                </label>
                <div className="mt-1.5 flex gap-3 items-start">
                  <div className="w-32 h-20 rounded-lg overflow-hidden bg-neutral-900 flex-shrink-0 flex items-center justify-center border border-white/10">
                    {form.thumbnail ? (
                      <img
                        src={resolveImageUrl(form.thumbnail)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImageOff className="w-6 h-6 text-neutral-700" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input
                      value={form.thumbnail}
                      onChange={(e) => setForm({ ...form, thumbnail: e.target.value })}
                      placeholder="URL gambar atau upload..."
                      className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white"
                    />
                    <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#D4A94F]/15 border border-[#D4A94F]/30 text-[#E9C46A] text-xs font-semibold cursor-pointer hover:bg-[#D4A94F]/25 btn-transition">
                      {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <UploadIcon className="w-3 h-3" />}
                      Upload File
                      <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                  Excerpt (Ringkasan)
                </label>
                <Textarea
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  rows={2}
                  className="bg-black/50 border-white/10 focus-visible:ring-[#D4A94F] text-white mt-1.5 resize-none"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                  Konten (Rich Text Editor)
                </label>
                <p className="text-[11px] text-neutral-500 mt-1 mb-1.5">
                  Tip: Klik ikon gambar di toolbar untuk mengunggah & menyisipkan gambar langsung ke dalam artikel.
                  {contentUploading && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[#E9C46A]">
                      <Loader2 className="w-3 h-3 animate-spin" /> Mengunggah gambar...
                    </span>
                  )}
                </p>
                <div className="mt-1.5 quill-dark" data-testid="blog-rte">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={form.content || ''}
                    onChange={(v) => setForm({ ...form, content: v })}
                    modules={quillModules}
                    formats={[
                      'header',
                      'bold',
                      'italic',
                      'underline',
                      'list',
                      'blockquote',
                      'link',
                      'image',
                    ]}
                    placeholder="Tulis konten artikel di sini..."
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="blog-published"
                  checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  className="w-4 h-4 accent-[#D4A94F]"
                />
                <label htmlFor="blog-published" className="text-sm text-neutral-300">
                  Publish (tampilkan di website)
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 p-6 border-t border-white/[0.06]">
              <Button variant="ghost" onClick={() => setModal(false)} className="text-neutral-400 hover:text-white">
                Batal
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="rounded-full bg-gradient-to-r from-[#E9C46A] to-[#D4A94F] text-black font-semibold btn-transition"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Simpan'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManager;
